import React from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

import { useLanguage } from '../../context/LanguageContext';
import DataConfidenceBanner from '../../components/DataConfidenceBanner';
import DataArchaeologyPanel from '../../components/DataArchaeologyPanel';
import AsIsSummaryStrip from '../../components/project/AsIsSummaryStrip';
import SeasonalSaturationCurve from '../../components/project/SeasonalSaturationCurve';
import InfoHint from '../../components/common/InfoHint';
import { useProject } from './ProjectContext';
import { useProjectAnalysis } from './useProjectAnalysis';

/**
 * Overview / As-Is (Part 0).
 *
 * The As-Is photo: Part 0 (data archaeology + design days) is the protagonist,
 * with a summary strip whose result headlines link out to the module that owns
 * the detail (Equilibrium, AP-CAM) rather than duplicating it here, followed by
 * the traffic photo (distribution + banks). The capacity-loss stratigraphy that
 * used to live here is analysis, not As-Is, so it moved to AP-CAM.
 */
const OverviewPage: React.FC = () => {
  const { project } = useProject();
  const { apcas, safeNum, part3, depDist, depDistData } = useProjectAnalysis();
  const { language } = useLanguage();
  const es = language === 'es';

  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';
  // Recharts needs concrete colours; read them off the design tokens so the
  // charts track the theme like everything else.
  const axis = 'var(--text-tertiary)';
  const grid = 'var(--border-subtle)';

  return (
    <div className="space-y-6 animate-fade-in">
      <DataConfidenceBanner project={project} />

      {/* Result headlines that route to their module */}
      <AsIsSummaryStrip part3={part3} apcas={apcas} safeNum={safeNum} language={es ? 'es' : 'en'} />

      {/* Part 0 — the As-Is protagonist */}
      <DataArchaeologyPanel project={project} />

      {/* Seasonal saturation — design-day peak demand vs the Tri-Node CC (SVS) */}
      <SeasonalSaturationCurve project={project} part3={part3} language={es ? 'es' : 'en'} />

      {/* Traffic photo — movement distribution (busy day) */}
      <div className="pro-card p-6">
        <div className="flex flex-wrap justify-between items-start mb-4 gap-3">
          <div>
            <h3 className={`font-bold text-lg font-display tracking-tight ${textMain} flex items-center gap-1.5`}>
              {es ? 'Distribución de Movimientos (5 min bins)' : 'Movement Distribution (5 min bins)'}
              <InfoHint
                align="left"
                text={es
                  ? 'Llegadas y salidas del IATA Busy Day (el día más cargado del año), en intervalos de 5 minutos. El "Peak" que se muestra es el máximo por hora de ESE día — por eso es mayor que el pico de los días de diseño mensuales (días distintos) y muy superior a la capacidad controladora (CC), que es lo que el sistema puede sostener de forma sostenida.'
                  : 'Arrivals and departures of the IATA Busy Day (the busiest day of the year), in 5-minute bins. The "Peak" shown is the hourly maximum of THAT day — that is why it is higher than the monthly design-day peaks (different days) and well above the controlling capacity (CC), which is what the system can sustain steadily.'}
              />
            </h3>
            {depDist.busy_day && (
              <p className={`text-xs mt-1 ${textSub}`}>
                {es ? 'IATA Busy Day: ' : 'IATA Busy Day: '}
                <span className="font-bold font-mono">{depDist.busy_day}</span>
                {depDist.total_arrivals != null && (
                  <span> — {depDist.total_arrivals} {es ? 'llegadas' : 'arr'} + {depDist.total_departures} {es ? 'salidas' : 'dep'}</span>
                )}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase">
              <div className="w-3 h-3 rounded-full" style={{ background: 'var(--node-rrc)' }}></div>
              <span className={textMain}>{es ? 'Llegadas' : 'Arrivals'}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase">
              <div className="w-3 h-3 rounded-full" style={{ background: 'var(--node-ptbc)' }}></div>
              <span className={textMain}>{es ? 'Salidas' : 'Departures'}</span>
            </div>
            {depDist.peak_hour != null && (
              <span className="badge" style={{ background: 'color-mix(in srgb, var(--aipron-accent) 12%, transparent)', color: 'var(--aipron-accent)' }}>
                Peak: {depDist.peak_hour} mov/h @ {depDist.peak_hour_start}
              </span>
            )}
          </div>
        </div>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={depDistData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={grid} />
              <XAxis dataKey="time" stroke={axis} fontSize={10} tickLine={false} axisLine={false} interval={11} angle={-45} textAnchor="end" height={60} />
              <YAxis stroke={axis} fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)', borderRadius: '8px', color: 'var(--text-primary)' }}
                itemStyle={{ color: 'var(--text-primary)' }}
                formatter={(value: number, name: string) => {
                  const label = name === 'arrivals' ? (es ? 'Llegadas' : 'Arrivals')
                    : name === 'departures' ? (es ? 'Salidas' : 'Departures')
                      : 'Total';
                  return [value, label];
                }}
              />
              <Area type="monotone" dataKey="arrivals" stroke="var(--node-rrc)" fill="var(--node-rrc)" fillOpacity={0.3} stackId="movements" />
              <Area type="monotone" dataKey="departures" stroke="var(--node-ptbc)" fill="var(--node-ptbc)" fillOpacity={0.3} stackId="movements" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
};

export default OverviewPage;
